import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logger from '../lib/logger';
import { Link } from 'react-router-dom';
import { apiGet } from '../lib/api';
import { useTranslation } from 'react-i18next';
import { MapPin, Star, BadgeDollarSign, Briefcase, Check, Filter, X } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Checkbox from '@radix-ui/react-checkbox';
import { slugify } from '../lib/slug';
import { getPublicDisplayName, initialsOf } from '../utils/displayName';
import { useCurrency } from '../context/CurrencyContext';


export default function Creatives() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { formatPrice, convert, currency: userCurrency } = useCurrency();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [minRate, setMinRate] = useState('');
  const [maxRate, setMaxRate] = useState('');
  const [minRating, setMinRating] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [skillsCatalog, setSkillsCatalog] = useState([]);

  const [selectedSkillIds, setSelectedSkillIds] = useState([]);
  const selectedSkillNames = useMemo(() => {
    const idToName = new Map((Array.isArray(skillsCatalog) ? skillsCatalog : []).map((s) => [String(s.id), s.name]));
    return (Array.isArray(selectedSkillIds) ? selectedSkillIds : [])
      .map((id) => idToName.get(String(id)))
      .filter(Boolean);
  }, [skillsCatalog, selectedSkillIds]);
  const [countryCode, setCountryCode] = useState('');
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState('');
  const [cityParamName, setCityParamName] = useState('');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Clear all filters function that resets both state and URL
  const clearAllFilters = () => {
    setSearch('');
    setMinRate('');
    setMaxRate('');
    setMinRating('');
    setCountryCode('');
    setSelectedCityId('');
    setSelectedSkillIds([]);
    setCityParamName('');
    // Reset URL to default (no query parameters)
    navigate('/creatives', { replace: true });
  };

  // Read filters from URL query on mount and when location changes
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const cc = params.get('country') || params.get('country_code');
    const cid = params.get('city_id');
    const q = params.get('q');
    const cityName = params.get('city');
    
    // Always update state from URL parameters
    setCountryCode(cc || '');
    setSelectedCityId(cid || '');
    setSearch(q || '');
    setCityParamName(cityName || '');
  }, [location.search, location.pathname]);

  // Debounce search input to avoid spamming the API
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(h);
  }, [search]);

  // Load countries once for stable, full-name dropdown
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await apiGet('/countries', { limit: 1000 });
        const list = res?.data?.countries || res?.countries || res?.data || [];
        if (mounted) setCountries(Array.isArray(list) ? list : []);
      } catch (e) {
        logger.warn('Failed to fetch countries', e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load cities for dropdown only once a country is selected
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!countryCode) {
          if (mounted) setCities([]);
          return;
        }
        const params = { limit: 1000, country: countryCode };
        const res = await apiGet('/cities', params);
        const list = res?.data?.cities || res?.cities || [];
        if (mounted) setCities(list);
      } catch (e) {
        logger.error(e);
      }
    })();
    return () => { mounted = false; };
  }, [countryCode]);

  // If a city name was provided via query (e.g., from AI assistant), map it to city_id once cities are loaded
  useEffect(() => {
    if (!cityParamName || !Array.isArray(cities) || cities.length === 0) return;
    const target = String(cityParamName).toLowerCase().trim();
    const match = cities.find((c) => String(c?.name || '').toLowerCase() === target);
    if (match && match.id) {
      setSelectedCityId(String(match.id));
      setCityParamName(''); // clear after applying
    }
  }, [cityParamName, cities]);

  // Reset city selection when country changes
  useEffect(() => {
    setSelectedCityId('');
  }, [countryCode]);

  // Load talents with server-side filters
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const params = {};
        // Build trimmed query tokens to avoid over-restricting server search
        const normalizeWord = (w) => {
          let x = String(w || '').toLowerCase();
          x = x.replace(/ers$/,'er');
          x = x.replace(/ists$/,'ist');
          x = x.replace(/s$/,'');
          x = x.replace(/ing$/,'');
          x = x.replace(/er$/,'');
          return x;
        };
        const stopwords = new Set(['find','looking','for','need','want','to','hire','book','get','a','an','the','search','seek']);
        const q = String(debouncedSearch || '').trim().toLowerCase();
        const qTokensRaw = q ? q.replace(/[^a-zA-ZÅÄÖåäö\-\s]/g, ' ').split(/\s+/).filter(Boolean) : [];
        const qTokens = qTokensRaw.map(normalizeWord).filter((tok) => tok && !stopwords.has(tok));
        const compactQuery = qTokens.join(' ').trim();
        // Only pass server-side search for compact queries (<= 2 tokens) to avoid empty results
        if (compactQuery && qTokens.length <= 2) params.search = compactQuery;
        if (minRate) params.min_rate = minRate;
        if (maxRate) params.max_rate = maxRate;
        if (minRating) params.min_rating = minRating;
        if (countryCode) params.country_code = countryCode;
        if (selectedCityId) params.city_id = selectedCityId;
        if (selectedSkillIds.length > 0) params.skill_ids = selectedSkillIds.join(',');
        let [creativesRes, skillsRes, creativeSkillsRes, servicesRes, catsRes] = await Promise.all([
          apiGet('/creatives', params),
          apiGet('/skills', { limit: 1000 }),
          apiGet('/creative-skills', { limit: 1000 }),
          apiGet('/services', { limit: 1000 }),
          apiGet('/categories', { limit: 1000 }),
        ]);
        let talents = creativesRes?.data?.creatives || [];
        // Fallback: if query exists and server returned empty, refetch without search and filter client-side
        if ((!Array.isArray(talents) || talents.length === 0) && q) {
          try {
            const fallbackParams = { ...params };
            delete fallbackParams.search;
            const fbRes = await apiGet('/creatives', fallbackParams);
            talents = fbRes?.data?.creatives || [];
          } catch (e) { void e; }
        }
        const skills = skillsRes?.data?.skills || [];
        const creativeSkills = creativeSkillsRes?.data?.creative_skills || [];
        const servicesList = servicesRes?.data?.services || servicesRes?.services || [];
        const categoriesList = catsRes?.data?.categories || catsRes?.categories || [];

        // Build maps for skills augmentation
        const skillNameById = new Map(skills.map((s) => [s.id, s.name]));
        const skillsByCreativeId = new Map();
        for (const cs of creativeSkills) {
          const list = skillsByCreativeId.get(cs.creative_id) || [];
          const nm = skillNameById.get(cs.skill_id);
          if (nm) list.push({ id: cs.skill_id, name: nm });
          skillsByCreativeId.set(cs.creative_id, list);
        }

        // Build services/category augmentation similar to TalentMap
        const ids = (Array.isArray(talents) ? talents : []).map((t) => t.id).filter(Boolean);
        const categoryNameById = new Map();
        (Array.isArray(categoriesList) ? categoriesList : []).forEach((c) => {
          if (c && (c.id != null)) categoryNameById.set(c.id, c.name || String(c));
        });
        const serviceTitlesByTalent = new Map();
        const categoryNamesByTalent = new Map();
        (Array.isArray(servicesList) ? servicesList : []).forEach((svc) => {
          const tid = svc?.creative_id;
          if (!tid || !ids.includes(tid)) return;
          const title = svc?.title || svc?.name || '';
          if (title) {
            const arr = serviceTitlesByTalent.get(tid) || [];
            arr.push(title);
            serviceTitlesByTalent.set(tid, arr);
          }
          const catId = svc?.category_id;
          const catName = categoryNameById.get(catId);
          if (catName) {
            const carr = categoryNamesByTalent.get(tid) || [];
            if (!carr.includes(catName)) carr.push(catName);
            categoryNamesByTalent.set(tid, carr);
          }
        });

        const augmented = (Array.isArray(talents) ? talents : []).map((it) => ({
          ...it,
          skills: skillsByCreativeId.get(it.id) || [],
          services: serviceTitlesByTalent.get(it.id) || it.services || [],
          categories: categoryNamesByTalent.get(it.id) || it.categories || [],
        }));

        if (mounted) {
          setItems(augmented);
          setSkillsCatalog(skills);
        }
      } catch (e) {
        logger.error(e);
        if (mounted) setError('Failed to load creatives');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [debouncedSearch, minRate, maxRate, minRating, countryCode, selectedCityId, selectedSkillIds]);

  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      const name = it.user?.display_name || '';
      const title = it.professional_title || it.user?.title || '';
      const bio = it.bio || it.about || it.description || it.tagline || it.short_description || '';
      const city = it.city_name || '';
      const country = it.user?.country_code || '';
      const skillNames = (Array.isArray(it.skills) ? it.skills.map((s) => s?.name || s) : []).filter(Boolean);
      const serviceNames = (Array.isArray(it.services) ? it.services.map((s) => s?.name || s?.title || s) : []).filter(Boolean);
      const categoryNames = (Array.isArray(it.categories) ? it.categories.map((c) => c?.name || c) : []).filter(Boolean);
      const rawText = [name, title, bio, city, country, ...skillNames, ...serviceNames, ...categoryNames]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const normalizeWord = (w) => {
        let x = String(w || '').toLowerCase();
        x = x.replace(/ers$/,'er');
        x = x.replace(/ists$/,'ist');
        x = x.replace(/s$/,'');
        x = x.replace(/ing$/,'');
        x = x.replace(/er$/,'');
        return x;
      };
      const synonymsRaw = {
        writer: ['writer','copywriter','copywriting','content writer','journalist'],
        editor: ['editor','editing','proofreader','proofreading'],
        photographer: ['photographer','photography','photo','photoshoot','portrait','wedding photography','camera'],
        videographer: ['videographer','videography','video','filmmaker','film'],
        designer: ['designer','design','ui','ux','ui ux','graphic','graphics','illustrator','illustration'],
        seo: ['seo','search engine optimization','search optimization'],
        marketer: ['marketing','marketer','digital marketing','social media'],
        branding: ['branding','brand','brand identity'],
      };
      // Normalize synonyms to align with normalized token comparison
      const synonyms = Object.fromEntries(Object.entries(synonymsRaw).map(([k, arr]) => {
        const nk = normalizeWord(k);
        const normArr = Array.from(new Set(arr.map(normalizeWord)));
        return [nk, normArr];
      }));
      const textTokens = rawText.replace(/[^a-zA-ZÅÄÖåäö\-\s]/g, ' ').split(/\s+/).filter(Boolean).map(normalizeWord);
      const textSet = new Set(textTokens);
      const q = String(debouncedSearch || '').trim().toLowerCase();
      const queryTokensRaw = q ? q.replace(/[^a-zA-ZÅÄÖåäö\-\s]/g, ' ').split(/\s+/).filter(Boolean) : [];
      const stopwords = new Set(['find','looking','for','need','want','to','hire','book','get','a','an','the','search','seek']);
      const queryTokens = queryTokensRaw.map(normalizeWord).filter((tok) => tok && !stopwords.has(tok));

      // Build expanded alternatives: treat synonyms as OR tokens; split multi-word synonyms
      const altTokens = new Set();
      queryTokens.forEach((qt) => {
        Object.entries(synonyms).forEach(([key, arr]) => {
          if (qt === key || arr.includes(qt)) {
            arr.forEach((syn) => {
              String(syn || '')
                .toLowerCase()
                .split(/\s+/)
                .map(normalizeWord)
                .filter(Boolean)
                .forEach((piece) => altTokens.add(piece));
            });
            // Also include the group key itself
            String(key || '')
              .toLowerCase()
              .split(/\s+/)
              .map(normalizeWord)
              .filter(Boolean)
              .forEach((piece) => altTokens.add(piece));
          }
        });
      });

      const candidateTokens = new Set([...(queryTokens || []), ...(Array.from(altTokens) || [])]);
      let matchesSearch = true;
      if (q) {
        const candidates = Array.from(candidateTokens);
        const present = candidates.reduce((acc, t) => acc + (textSet.has(t) ? 1 : 0), 0);
        const baseLen = Math.max(1, queryTokens.length);
        const baseRatio = present / baseLen;
        const anyHit = present >= 1;
        matchesSearch = anyHit || baseRatio >= 0.5 || rawText.includes(q);
      }
      const rate = Number(it.hourly_rate ?? 0);
      const rating = Number(it.rating ?? 0);
      const meetsMinRate = minRate ? rate >= Number(minRate) : true;
      const meetsMaxRate = maxRate ? rate <= Number(maxRate) : true;
      const meetsMinRating = minRating ? rating >= Number(minRating) : true;
      const meetsCountry = countryCode ? String(country).toLowerCase() === String(countryCode).toLowerCase() : true;
      const meetsCity = selectedCityId ? (it.user?.city_id === selectedCityId) : true;
      const hasSelectedSkills = selectedSkillIds.length > 0
        ? selectedSkillIds.some((sid) => (it.skills || []).some((s) => s.id === sid))
        : true;
      return matchesSearch && meetsMinRate && meetsMaxRate && meetsMinRating && meetsCountry && meetsCity && hasSelectedSkills;
    });
  }, [items, debouncedSearch, minRate, maxRate, minRating, countryCode, selectedCityId, selectedSkillIds]);

  const totalCount = filteredItems.length;
  const showingLabel = useMemo(() => t('talentsPage.showingCount', { count: totalCount }), [t, totalCount]);

  const countryOptions = useMemo(() => {
    const arr = Array.isArray(countries) ? countries : [];
    return arr.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [countries]);

  const cityOptions = useMemo(() => {
    const list = Array.isArray(cities) ? cities : [];
    const filtered = countryCode
      ? list.filter((c) => String(c.country_code || '').toLowerCase() === String(countryCode).toLowerCase())
      : list;
    return filtered.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [cities, countryCode]);

  const countryNameByCode = useMemo(() => {
    const map = new Map();
    (Array.isArray(countries) ? countries : []).forEach((c) => {
      if (c && c.code) map.set(String(c.code).toUpperCase(), c.name);
    });
    return map;
  }, [countries]);

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="rounded-xl p-6 md:p-8 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-primary-text">{t('talentsPage.heroTitle', { defaultValue: 'Meet Local Creatives' })}</h1>
        <p className="mt-2 text-lg text-secondary-text max-w-2xl mx-auto">{t('talentsPage.heroSubtitle', { defaultValue: 'Connect with local experts that can help your business with their knowledge and passion' })}</p>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-primary-text">{t('talentsPage.title', { defaultValue: 'Creatives' })}</h2>
        <div className="text-sm text-secondary-text">{showingLabel}</div>
      </div>

      {/* Filters */}
      <div className="bg-card-bg border border-card-border rounded-xl p-2 md:p-3 space-y-2">
        <div className="flex-1">
          <label className="block text-xs text-secondary-text mb-1" htmlFor="search">{t('talentsPage.searchLabel', { defaultValue: 'Search' })}</label>
          <input
            id="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-9 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text"
            placeholder={t('talentsPage.searchPlaceholder', { defaultValue: t('talentMap.ui.searchPlaceholder') })}
          />
        </div>
        <div className="hidden lg:grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 lg:grid-cols-12 items-end gap-2 md:gap-3 lg:gap-4">
          {/* Skills */}
          <div className="min-w-0 sm:col-span-3 md:col-span-3 lg:col-span-5">
            <label className="hidden md:block text-xs text-secondary-text mb-1">{t('talentsPage.filters.skills', { defaultValue: 'Skills' })}</label>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger className="w-full h-9 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text text-left truncate" title={selectedSkillNames.join(', ')}>
                {selectedSkillNames.length === 0
                  ? t('talentsPage.selectSkills', { defaultValue: 'Select skills' })
                  : (selectedSkillNames.length <= 3
                    ? selectedSkillNames.join(', ')
                    : t('talentsPage.selectedSkillsCount', { count: selectedSkillNames.length, defaultValue: `${selectedSkillNames.length} selected` }))}
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content className="bg-card-bg border border-card-border rounded-lg shadow-lg p-2 w-[280px] max-h-[300px] overflow-y-auto" sideOffset={6}>
                  <div className="flex items-center justify-between px-1 pb-2">
                    <div className="text-xs text-secondary-text">
                      {t('talentsPage.selectedSkillsCount', { count: selectedSkillIds.length, defaultValue: `${selectedSkillIds.length} selected` })}
                    </div>
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => setSelectedSkillIds([])}
                    >
                      {t('common.clear', { defaultValue: 'Clear' })}
                    </button>
                  </div>
                  <div className="space-y-1">
                    {skillsCatalog.map((s) => {
                      const checked = selectedSkillIds.includes(s.id);
                      return (
                        <DropdownMenu.Item key={s.id} asChild>
                          <div className="flex items-center gap-2 px-2 py-1 rounded hover:bg-secondary-bg cursor-pointer">
                            <Checkbox.Root
                              checked={checked}
                              onCheckedChange={(v) => {
                                const isChecked = v === true;
                                setSelectedSkillIds((prev) => {
                                  const has = prev.includes(s.id);
                                  if (isChecked) return has ? prev : [...prev, s.id];
                                  return prev.filter((x) => x !== s.id);
                                });
                              }}
                              className="w-4 h-4 shrink-0 rounded border border-card-border bg-input-bg data-[state=checked]:bg-primary data-[state=checked]:border-primary flex items-center justify-center"
                              aria-label={s.name}
                            >
                              <Checkbox.Indicator>
                                <Check className="w-3 h-3 text-white" />
                              </Checkbox.Indicator>
                            </Checkbox.Root>
                            <span className="text-sm text-primary-text">{s.name}</span>
                          </div>
                        </DropdownMenu.Item>
                      );
                    })}
                  </div>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
          {/* Location: Country + City */}
          <div className="min-w-0 sm:col-span-3 md:col-span-3 lg:col-span-5">
            <label className="hidden md:block text-xs text-secondary-text mb-1">{t('talentsPage.location', { defaultValue: 'Location' })}</label>
            <div className="flex gap-2 min-w-0">
              <select
                id="country"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="flex-1 h-9 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text"
              >
                <option value="">{t('talentsPage.any', { defaultValue: 'All' })}</option>
                {countryOptions.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
              <select
                id="city"
                value={selectedCityId}
                onChange={(e) => setSelectedCityId(e.target.value)}
                className="flex-1 h-9 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text"
                disabled={!countryCode}
              >
                <option value="">{t('talentsPage.any', { defaultValue: 'All' })}</option>
                {countryCode && cityOptions.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Rating */}
          <div className="min-w-0 sm:col-span-1 md:col-span-2 lg:col-span-2">
            <label className="hidden md:block text-xs text-secondary-text mb-1" htmlFor="minRating">{t('talentsPage.rating', { defaultValue: 'Rating' })}</label>
            <select
              id="minRating"
              value={minRating}
              onChange={(e) => setMinRating(e.target.value)}
              className="w-full h-9 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text"
            >
              <option value="">{t('talentsPage.any', { defaultValue: 'All' })}</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
              <option value="4">4+</option>
              <option value="5">5+</option>
            </select>
          </div>
          {/* Rate Range: Min + Max */}
          <div className="min-w-0 sm:col-span-2 md:col-span-2 lg:col-span-4">
            <label className="hidden md:block text-xs text-secondary-text mb-1">{t('talentsPage.rateRange', { defaultValue: 'Rate Range' })}</label>
            <div className="flex gap-2 min-w-0">
              <input
                id="minRate"
                type="number"
                min="0"
                value={minRate}
                onChange={(e) => setMinRate(e.target.value)}
                className="flex-1 lg:flex-none lg:w-24 h-9 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text"
                placeholder={t('talentsPage.minRate', { defaultValue: 'Min' })}
              />
              <input
                id="maxRate"
                type="number"
                min="0"
                value={maxRate}
                onChange={(e) => setMaxRate(e.target.value)}
                className="flex-1 lg:flex-none lg:w-24 h-9 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text"
                placeholder={t('talentsPage.maxRate', { defaultValue: 'Max' })}
              />
            </div>
          </div>
          {/* Clear button aligned at end */}
          <div className="min-w-0 sm:col-span-3 md:col-span-6 lg:col-span-8 self-end justify-self-end">
            <button
              onClick={clearAllFilters}
              className="btn btn-outline btn-sm md:btn-md"
            >
              {t('talentsPage.clear', { defaultValue: 'Clear' })}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Filters Trigger (md:hidden) */}
      {!isFiltersOpen && (
        <button
          onClick={() => setIsFiltersOpen(true)}
          className="lg:hidden fixed top-24 md:top-28 right-0 z-40 rounded-l-full rounded-r-none border border-r-0 border-zinc-300 dark:border-zinc-700 bg-card-bg/90 backdrop-blur p-3 shadow-xl hover:bg-card-bg hover:shadow-2xl transition transform hover:-translate-y-0.5 text-primary-text inline-flex items-center"
          aria-label={t('talentsPage.filters', { defaultValue: 'Filters' })}
          aria-expanded={isFiltersOpen}
        >
          <Filter size={20} />
        </button>
      )}

      {/* Mobile Off-canvas Filters (md:hidden) */}
      {isFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsFiltersOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-card-bg border-l border-card-border shadow-xl p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-primary-text">{t('talentsPage.filters.title', { defaultValue: 'Filters' })}</div>
              <button onClick={() => setIsFiltersOpen(false)} className="text-secondary-text" aria-label={t('common.close', { defaultValue: 'Close' })}><X size={16} /></button>
            </div>

            <div className="space-y-4">
              {/* Skills */}
               <div>
                 <label className="text-xs text-secondary-text mb-1 block">{t('talentsPage.filters.skills', { defaultValue: 'Skills' })}</label>
                 <DropdownMenu.Root>
                   <DropdownMenu.Trigger className="w-full h-10 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text text-left truncate" title={selectedSkillNames.join(', ')}>
                     {selectedSkillNames.length === 0
                       ? t('talentsPage.selectSkills', { defaultValue: 'Select skills' })
                       : (selectedSkillNames.length <= 3
                         ? selectedSkillNames.join(', ')
                         : t('talentsPage.selectedSkillsCount', { count: selectedSkillNames.length, defaultValue: `${selectedSkillNames.length} selected` }))}
                   </DropdownMenu.Trigger>
                   <DropdownMenu.Portal>
                     <DropdownMenu.Content className="bg-card-bg border border-card-border rounded-lg shadow-lg p-2 w-[280px] max-h-[300px] overflow-y-auto" sideOffset={6}>
                       <div className="flex items-center justify-between px-1 pb-2">
                         <div className="text-xs text-secondary-text">
                           {t('talentsPage.selectedSkillsCount', { count: selectedSkillIds.length, defaultValue: `${selectedSkillIds.length} selected` })}
                         </div>
                         <button type="button" className="text-xs text-primary hover:underline" onClick={() => setSelectedSkillIds([])}>
                           {t('common.clear', { defaultValue: 'Clear' })}
                         </button>
                       </div>
                       <div className="space-y-1">
                         {skillsCatalog.map((s) => {
                           const checked = selectedSkillIds.includes(s.id);
                           return (
                             <DropdownMenu.Item key={s.id} asChild>
                               <div className="flex items-center gap-2 px-2 py-1 rounded hover:bg-secondary-bg cursor-pointer">
                                 <Checkbox.Root
                                   checked={checked}
                                   onCheckedChange={(v) => {
                                     const isChecked = v === true;
                                     setSelectedSkillIds((prev) => {
                                       const has = prev.includes(s.id);
                                       if (isChecked) return has ? prev : [...prev, s.id];
                                       return prev.filter((x) => x !== s.id);
                                     });
                                   }}
                                   className="w-4 h-4 shrink-0 rounded border border-card-border bg-input-bg data-[state=checked]:bg-primary data-[state=checked]:border-primary flex items-center justify-center"
                                   aria-label={s.name}
                                 >
                                   <Checkbox.Indicator>
                                     <Check className="w-3 h-3 text-white" />
                                   </Checkbox.Indicator>
                                 </Checkbox.Root>
                                 <span className="text-sm text-primary-text">{s.name}</span>
                               </div>
                             </DropdownMenu.Item>
                           );
                         })}
                       </div>
                     </DropdownMenu.Content>
                   </DropdownMenu.Portal>
                 </DropdownMenu.Root>
               </div>

               {/* Location */}
               <div>
                 <label className="text-xs text-secondary-text mb-1 block">{t('talentsPage.location', { defaultValue: 'Location' })}</label>
                 <div className="flex gap-2 min-w-0">
                   <select
                     id="m-country"
                     value={countryCode}
                     onChange={(e) => setCountryCode(e.target.value)}
                     className="flex-1 h-10 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text"
                   >
                     <option value="">{t('talentsPage.any', { defaultValue: 'All' })}</option>
                     {countryOptions.map((c) => (
                       <option key={c.code} value={c.code}>{c.name}</option>
                     ))}
                   </select>
                   <select
                     id="m-city"
                     value={selectedCityId}
                     onChange={(e) => setSelectedCityId(e.target.value)}
                     className="flex-1 h-10 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text"
                     disabled={!countryCode}
                   >
                     <option value="">{t('talentsPage.any', { defaultValue: 'All' })}</option>
                     {countryCode && cityOptions.map((c) => (
                       <option key={c.id} value={c.id}>{c.name}</option>
                     ))}
                   </select>
                 </div>
               </div>

               {/* Rating */}
               <div>
                 <label className="text-xs text-secondary-text mb-1 block" htmlFor="m-minRating">{t('talentsPage.rating', { defaultValue: 'Rating' })}</label>
                 <select
                   id="m-minRating"
                   value={minRating}
                   onChange={(e) => setMinRating(e.target.value)}
                   className="w-full h-10 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text"
                 >
                   <option value="">{t('talentsPage.any', { defaultValue: 'All' })}</option>
                   <option value="1">1+</option>
                   <option value="2">2+</option>
                   <option value="3">3+</option>
                   <option value="4">4+</option>
                   <option value="5">5+</option>
                 </select>
               </div>

               {/* Rate Range */}
               <div>
                 <label className="text-xs text-secondary-text mb-1 block">{t('talentsPage.rateRange', { defaultValue: 'Rate Range' })}</label>
                 <div className="grid grid-cols-2 gap-2 min-w-0">
                   <input
                     id="m-minRate"
                     type="number"
                     min="0"
                     value={minRate}
                     onChange={(e) => setMinRate(e.target.value)}
                     className="w-full min-w-0 h-10 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text"
                     placeholder={t('talentsPage.minRate', { defaultValue: 'Min' })}
                   />
                   <input
                     id="m-maxRate"
                     type="number"
                     min="0"
                     value={maxRate}
                     onChange={(e) => setMaxRate(e.target.value)}
                     className="w-full min-w-0 h-10 rounded-lg bg-input-bg border border-card-border px-3 text-primary-text"
                     placeholder={t('talentsPage.maxRate', { defaultValue: 'Max' })}
                   />
                 </div>
               </div>

               {/* Clear */}
               <div className="pt-1">
                 <button
                   onClick={clearAllFilters}
                   className="btn btn-outline btn-sm w-full"
                 >
                   {t('talentsPage.clear', { defaultValue: 'Clear' })}
                 </button>
               </div>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="text-sm text-secondary-text">{t('talentsPage.loading', { defaultValue: 'Loading...' })}</div>}
      {error && <div className="text-sm text-red-400">{error}</div>}

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.length === 0 && !loading ? (
          <div className="text-sm text-secondary-text">{t('talentsPage.noResults', { defaultValue: 'No creatives found' })}</div>
        ) : (
          filteredItems.map((talent) => (
            <div key={talent.id} className="bg-card-bg card-hover rounded-xl shadow-md border border-card-border h-full flex flex-col px-3 pt-3 pb-3 overflow-hidden">
              {/* Card Header with gradient background */}
              <div className="card-header-gradient rounded-t-xl -mx-3 -mt-3 mb-3 p-3">
                <div className="flex items-center gap-3">
                  {talent.user?.avatar_url ? (
                    <img
                      src={talent.user?.avatar_url}
                      alt={`${getPublicDisplayName(talent.user) || 'Creative'} - Profile picture`}
                      className="w-14 h-14 rounded-full border border-card-border object-cover"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full border border-card-border bg-secondary-bg flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-text">
                        {initialsOf(getPublicDisplayName(talent.user))}
                      </span>
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-primary-text">{getPublicDisplayName(talent.user) || 'Unknown'}</div>
                      {talent.is_featured && <span className="text-xs text-primary">{t('talentsPage.featured', { defaultValue: 'Featured' })}</span>}
                    </div>
                    {talent.professional_title && (
                      <div className="mt-0.5 text-xs text-secondary-text flex items-center gap-1">
                        <Briefcase size={14} /> {talent.professional_title}
                      </div>
                    )}
                    <div className="mt-0.5 text-xs text-secondary-text flex items-center gap-1">
                      <MapPin size={14} /> {talent.city_name || '-'}, {countryNameByCode.get(String(talent.user?.country_code || '').toUpperCase()) || (talent.user?.country_code || '-')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="flex-1 flex flex-col">
                {Array.isArray(talent.skills) && talent.skills.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {talent.skills.slice(0, 6).map((s) => (
                      <span key={s.id} className="inline-flex items-center text-xs px-2 py-1 rounded-md bg-secondary-bg text-secondary-text border border-card-border">
                        {s.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between pt-3 px-0 pb-0">
                <div className="flex items-center gap-3 text-xs text-secondary-text">
                  <span className="inline-flex items-center gap-1"><Star size={14} className="text-yellow-500" />{talent.rating ?? 0} ({talent.total_reviews ?? 0})</span>
                  <span className="inline-flex items-center gap-1"><BadgeDollarSign size={14} />{talent.hourly_rate != null ? formatPrice(convert(talent.hourly_rate, talent.currency || 'SEK', userCurrency), userCurrency) : 'N/A'}</span>
                </div>
                <Link
                  to={`/creative/profile/${(() => {
                    const name = getPublicDisplayName(talent?.user) || '';
                    const baseSlug = slugify(name || '');
                    const rawId = talent?.user?.id || talent?.id || '';
                    const idText = String(rawId).toLowerCase().replace(/[^0-9a-f]/g, '');
                    const short = `${idText.slice(0, 6)}${idText.slice(-6)}`;
                    return `${baseSlug}-${short}`;
                  })()}`}
                  className="btn btn-primary"
                >
                   View Profile
                 </Link>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Bottom CTA */}
      <div className="rounded-xl p-6 md:p-8 text-center">
        <h3 className="text-2xl md:text-3xl font-bold text-primary-text">{t('about.title', { defaultValue: 'Join Our Community' })}</h3>
        <p className="mt-2 text-secondary-text max-w-2xl mx-auto">{t('about.text', { defaultValue: 'Be part of a global network of explorers, creators, and local talents.' })}</p>
        <div className="mt-6">
          <Link to="/register" className="cta-brand inline-flex items-center justify-center gap-2 px-6 py-3" data-variant="primary">
            {t('actions.register', { defaultValue: 'Register now!' })}
          </Link>
        </div>
      </div>
    </div>
  );
}
